import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../../generic-components/Button';
import Icon from '../../../../generic-components/Icon';
import { ReactComponent as EyeIcon } from '../../../../assets/icons/svg/shared/ic-shared-eye-opened.svg';
import uploadIcon from '../../../../assets/svg/upload-icon.svg';
import { useStore } from '../../../../store';
import { useUploadReceiptMutation } from '../../../../hooks/mutations/useUploadReceiptMutation';
import { useDownloadReceiptMutation } from '../../../../hooks/mutations/useDownloadReceiptMutation';
import { UserMessage } from '../../../../DesignSystem/LiliUserMessage/UserMessage';
import downloadIcon from '../../../../assets/svg/download-icon.svg';
import closeIcon from '../../../../assets/svg/shared/icon-close.svg';
import Spinner, { SpinnerColor } from '../../../../generic-components/Spinner';
import { TRANSACTION_DETAILS_TEXTS } from '../../constants';

interface IProps {
  handleOnSuccessUploadReceipt?: (value: string, property: string) => void;
  transactionId: string;
  receiptData?: string;
  canAddReceipt: boolean;
}

export const fileTypes = {
  PDF: 'PDF',
  IMAGE: 'IMAGE',
};

const TransactionReceipt = ({ transactionId, receiptData, canAddReceipt, handleOnSuccessUploadReceipt }: IProps) => {
  const { RECEIPT, REPLACE_RECEIPT } = TRANSACTION_DETAILS_TEXTS;
  const { customerId } = useStore((state) => state.user);
  const hiddenFileInput = useRef(null);
  const [formData, setFormData] = useState<FormData | null>(null);
  const [isPhotoShown, setIsPhotoShown] = useState<boolean>(false);
  const [receiptUrl, setReceiptUrl] = useState<string | undefined>('');
  const [haveReceiptUrl, setHaveReceiptUrl] = useState('');

  const { mutate: mutateUpload, isLoading } = useUploadReceiptMutation({
    customerId,
    transactionId,
    formData,
    onSuccess: (receiptPath: string) => {
      if (handleOnSuccessUploadReceipt && receiptPath) {
        handleOnSuccessUploadReceipt(receiptPath, 'receipt');
      }
      setHaveReceiptUrl(receiptPath);
    },
  });
  const {
    mutate: mutateDownload,
    data: downloadData,
    error: downloadError,
    isSuccess,
  } = useDownloadReceiptMutation({ customerId, transactionId });

  useEffect(() => {
    const closeReceipt = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsPhotoShown(false);
      }
    };
    window.addEventListener('keydown', closeReceipt);
    return () => window.removeEventListener('keydown', closeReceipt);
  }, []);

  useEffect(() => {
    if (isSuccess) {
      setReceiptUrl(downloadData?.url);
    }
  }, [isSuccess]);

  useEffect(() => {
    if (haveReceiptUrl) {
      mutateDownload();
    }
  }, [haveReceiptUrl]);

  useEffect(() => {
    setReceiptUrl('');
    setFormData(null);
    if (receiptData) {
      setHaveReceiptUrl(receiptData);
    } else {
      setHaveReceiptUrl('');
    }
  }, [transactionId]);

  useEffect(() => {
    if (!downloadError) return;
    setIsPhotoShown(false);
  }, [downloadError]);

  const handleUploadFile = (file: any) => {
    const data = new FormData();
    data.append('file', file, `Receipt-${transactionId}`);
    setFormData(data);
  };

  const handleFileInput = (file: any) => {
    if (!file) return;
    const localFileUrl = URL.createObjectURL(file);
    setReceiptUrl(localFileUrl);
    handleUploadFile(file);
  };

  const handleClick = () => {
    if (hiddenFileInput.current) {
      (hiddenFileInput.current as HTMLInputElement).click();
    }
  };

  const handleDownload = () => {
    if (downloadData?.url) {
      const a = document.createElement('a');
      a.href = downloadData.url;
      a.download = 'receipt';
      a.click();
      document.removeChild(a);
    }
  };

  useEffect(() => {
    if (!formData) return;
    mutateUpload();
  }, [formData]);

  const { t } = useTranslation();

  return (
    <div className='flex pt-6 w-full justify-between'>
      <div id='transactionDetailsReceiptTitle' className='font-metricMedium text-base'>
        {t(RECEIPT)}
      </div>
      <div className='flex gap-6 items-center'>
        {(haveReceiptUrl || isLoading) && (
          <Button
            id='transactionReceipt'
            onClick={() => {
              setIsPhotoShown((prevState) => !prevState);
            }}
            childElement={
              isLoading ? (
                <Spinner spinnerColor={SpinnerColor.Blue} height='1rem' width='1rem' />
              ) : (
                <EyeIcon id='view' className='text-bw07' />
              )
            }
            className=''
          />
        )}
        {isPhotoShown && (
          <UserMessage
            id='transactionReceiptUserMessage'
            mainWrapperClassName='items-start'
            contentWrapperClassName='w-screen h-screen'
            onClick={() => {
              setIsPhotoShown(!isPhotoShown);
            }}
          >
            {receiptUrl ? (
              <div className='w-full h-full flex flex-col items-center'>
                <div className='flex bg-bw08 rounded-lg mb-4 justify-between items-center w-full min-h-[70px]'>
                  <div id='transactionDetailsViewReceipt' className='text-base font-metricSemibold text-bw00 ml-[30px]'>
                    View Receipt
                  </div>
                  <div className='flex'>
                    <Button
                      id='replaceImage'
                      className='text-base text-bw00 font-metricMedium mr-[1.875rem] border-b'
                      title={t(REPLACE_RECEIPT)}
                      onClick={() => {
                        handleClick();
                      }}
                    />
                    <Button
                      id='handleDownload'
                      onClick={handleDownload}
                      childElement={<Icon src={downloadIcon} />}
                      className=''
                    />
                    <Button
                      id='closeTransactionReceipt'
                      onClick={() => {
                        setIsPhotoShown(!isPhotoShown);
                      }}
                      childElement={<Icon src={closeIcon} />}
                      className='mr-[20px]'
                    />
                  </div>
                </div>
                <div className={`h-full flex justify-center ${downloadData?.type === fileTypes.PDF ? 'w-[70%]' : ''}`}>
                  {downloadData?.type === fileTypes.IMAGE && (
                    <img
                      id='transactionDetailsReceiptImage'
                      className='max-h-[90%] max-w-[90%] '
                      src={receiptUrl}
                      alt='receiptImage'
                    />
                  )}
                  {downloadData?.type === fileTypes.PDF && (
                    <iframe
                      title='transactionDetailsReceiptImage'
                      id='transactionDetailsReceiptImage'
                      className='max-h-[90%] max-w-[90%] w-full'
                      src={receiptUrl}
                    />
                  )}
                </div>
              </div>
            ) : (
              <Spinner spinnerColor={SpinnerColor.White} height='3rem' width='3rem' />
            )}
          </UserMessage>
        )}
        {canAddReceipt && (
          <Button
            id='uploadTransactionReceipt'
            className='w-[1.5rem] h-[1.5rem]'
            onClick={handleClick}
            childElement={<Icon iconId='upload' src={uploadIcon} />}
          />
        )}
        <input
          id='handleFileInput'
          ref={hiddenFileInput}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            if (event.target.files) {
              handleFileInput(event?.target?.files[0]);
            }
          }}
          type='file'
          accept='image/*,.pdf'
          hidden
        />
      </div>
    </div>
  );
};

export default TransactionReceipt;
